import React, { useState, useEffect, useContext, createContext } from 'react';
import { IcFilters, IcClose, IcCalendar, IcChevronLeft, IcChevronRight, IcUndo } from '../../theme/icons';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// Sous-chemin exporté (webpack) ; jest le remappe vers le .cjs — voir
// moduleNameMapper dans package.json (la build ESM ne passe pas sous jest).
import { fr } from 'date-fns/locale/fr';
import { PERIODS } from './statsUi';

registerLocale('fr', fr);

// Filtre de période partagé par « Statistique de ventes » et « Statistique
// générale » — refonte owner 2026-09-10 (« plus simple, meilleure UX »), en
// gardant le cadre commun des listes : bouton « Filtres » + carte .flt-card.
//
//   [ 30 jours · 12 août – 10 sept. 2026 ]            [ ⚟ Filtres ]
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ (7 jours) (30 jours) (90 jours) (Ce mois) (Cette année)          │
//   │        Plage personnalisée  ‹ [ 📅 30 jours · 12 août – 10 sept. ▾ ] ›  ↶ Réinitialiser │
//   └──────────────────────────────────────────────────────────────────┘
//   (une seule rangée, owner 2026-09-10 ; elle se replie sur 2 lignes en étroit)
//
// - Plus de bouton Appliquer : une pilule = la page change ; dans le
//   calendrier, jour de début puis jour de fin → appliqué aussitôt.
// - Le calendrier est une LISTE DÉFILANTE de mois (owner 2026-09-10 :
//   « scroll inner is better ») : 12 mois empilés dans une boîte qui défile,
//   ouverte sur le mois courant (ou sur le début de la plage posée), en-tête
//   de chaque mois collé en haut pendant le défilement. Une plage de 90 jours
//   se prend en faisant défiler, sans changer de mois ni de largeur d'écran.
// - Le champ de période montre les vraies dates couvertes ; ‹ › = période
//   précédente / suivante de même longueur (mois civil précédent pour
//   « Ce mois », année précédente pour « Cette année »).
// - Sélection lisible (owner 2026-09-10 « more clear from day to other day ») :
//   bandeau d'aide collé en haut du calendrier (« Cliquez le jour de début » →
//   « Début : 12 août — cliquez le jour de fin »), bande verte continue entre
//   les deux jours, jours de début / fin en encre.
// - Plage libre active : ✕ dans le champ (ou lien Réinitialiser à côté du
//   bouton Filtres) pour revenir aux 30 jours ; badge « 1 » sur le bouton.
//
// filter = { period } (période rapide) OU { start, end } (plage libre, YYYY-MM-DD).

export const DEFAULT_FILTER = { period: '30d' };

// YYYY-MM-DD en heure locale pour l'API.
export const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const DAY_MS = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const parseIso = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Jours couverts par le filtre (miroir des fenêtres du backend, pos/api_views
// STAT_PERIODS) : « 30 jours » = les 30 derniers jours jusqu'à aujourd'hui,
// « Ce mois » = du 1er à aujourd'hui, « Cette année » = du 1er janvier.
export const resolveRange = (filter, today = new Date()) => {
  const t = startOfDay(today);
  if (filter.start && filter.end) return { start: parseIso(filter.start), end: parseIso(filter.end) };
  if (filter.period === 'month') return { start: new Date(t.getFullYear(), t.getMonth(), 1), end: t };
  if (filter.period === 'year') return { start: new Date(t.getFullYear(), 0, 1), end: t };
  const days = { '7d': 7, '30d': 30, '90d': 90 }[filter.period] || 30;
  return { start: addDays(t, -(days - 1)), end: t };
};

// Période précédente / suivante de même longueur. « Ce mois » ‹ = mois civil
// précédent entier ; « Cette année » ‹ = année civile précédente entière.
// Renvoie null quand la période suivante commencerait après aujourd'hui.
export const shiftFilter = (filter, dir, today = new Date()) => {
  const t = startOfDay(today);
  const { start, end } = resolveRange(filter, today);
  let ns; let ne;
  if (filter.period === 'month' || (filter.start && start.getDate() === 1 && isLastDayOfMonth(end))) {
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    ns = new Date(start.getFullYear(), start.getMonth() + dir * months, 1);
    ne = new Date(ns.getFullYear(), ns.getMonth() + months, 0);
  } else if (filter.period === 'year') {
    ns = new Date(start.getFullYear() + dir, 0, 1);
    ne = new Date(ns.getFullYear(), 11, 31);
  } else {
    const span = Math.round((end - start) / DAY_MS) + 1;
    ns = addDays(start, dir * span);
    ne = addDays(end, dir * span);
  }
  if (ns > t) return null;
  if (ne > t) ne = t;
  return { start: isoDay(ns), end: isoDay(ne) };
};

const isLastDayOfMonth = (d) => addDays(d, 1).getDate() === 1;

const fmtShort = (d, withYear) =>
  d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });

// « 11 août – 9 sept. 2026 » (année une seule fois quand elle est commune).
export const rangeLabel = ({ start, end }) => {
  if (start.getTime() === end.getTime()) return fmtShort(start, true);
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${fmtShort(start, !sameYear)} – ${fmtShort(end, true)}`;
};

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
// Mois empilés dans le calendrier défilant (1 an d'historique — 24 mois
// doublaient le coût de rendu de chaque clic dans le calendrier).
const MONTHS_SHOWN = 12;

// En-tête de chaque mois de la liste : « Mois AAAA », collé en haut de la
// boîte pendant le défilement (position: sticky, voir .stats-range-popper).
const CalendarHeader = ({ monthDate }) => (
  <div className="px-3 py-1.5 text-sm font-semibold text-gray-900 capitalize">
    {MONTHS_FR[monthDate.getMonth()]} {monthDate.getFullYear()}
  </div>
);

// Boîte du calendrier : bandeau d'aide collé en haut (au-dessus des en-têtes
// de mois) qui dit quoi cliquer, puis la liste des mois. Le composant est
// STABLE (défini une fois) et lit l'aide dans un contexte : recréer le
// conteneur à chaque rendu remonterait tout le calendrier et perdrait le
// défilement à chaque clic.
const HintContext = createContext('');
const CalendarContainer = ({ className, children }) => {
  const hint = useContext(HintContext);
  return (
    <div className={className}>
      <div className="stats-range-hint" role="status" aria-live="polite">{hint}</div>
      {children}
    </div>
  );
};

// Fait défiler la liste jusqu'au début de la plage (ou au mois courant, en
// bas) juste après l'ouverture — le popper est rendu hors du composant.
const scrollCalendarToRange = () => {
  const box = document.querySelector('.stats-range-popper .react-datepicker');
  if (!box) return;
  const target = box.querySelector('.react-datepicker__day--range-start, .react-datepicker__day--selected');
  const month = target && target.closest('.react-datepicker__month-container');
  box.scrollTop = month ? Math.max(0, month.offsetTop - 4) : box.scrollHeight;
};

// Champ de période : même cadre que la barre de recherche des listes (`inp`
// 48 px, focus encre). Libellé de la période en gras + dates couvertes en gris.
const RangeField = React.forwardRef(({ onClick, label, dates, isCustom, onClear, open }, ref) => (
  <div className="relative flex-1 min-w-0 sm:flex-none">
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Période : ${label}, ${dates}. Choisir une plage de dates`}
      className={`inp h-12 w-full sm:w-auto inline-flex items-center gap-2.5 pl-4 ${isCustom ? 'pr-11' : 'pr-4'} text-left cursor-pointer hover:border-gray-400 transition-colors`}
    >
      <IcCalendar size={15} className="text-gray-500 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate text-sm">
        <span className="font-semibold text-gray-900">{label}</span>
        <span className="hidden sm:inline text-gray-500"> · {dates}</span>
      </span>
      <IcChevronRight size={11} className="rotate-90 text-gray-400 shrink-0" aria-hidden="true" />
    </button>
    {isCustom && (
      <button
        type="button"
        onClick={onClear}
        aria-label="Revenir aux 30 jours"
        title="Revenir aux 30 jours"
        className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
      >
        <IcClose size={12} />
      </button>
    )}
  </div>
));

// `extra` : bouton(s) supplémentaire(s) rendus dans la rangée, avant le chip.
export const StatsFilterBar = ({ filter, onChange, extra = null }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [draftRange, setDraftRange] = useState([null, null]); // [Date, Date] pendant la sélection
  const [rangeOpen, setRangeOpen] = useState(false);
  useEffect(() => {
    if (!rangeOpen) return undefined;
    const t = setTimeout(scrollCalendarToRange, 0);
    return () => clearTimeout(t);
  }, [rangeOpen]);

  const isCustom = !!filter.start;
  const range = resolveRange(filter);
  const days = Math.round((range.end - range.start) / DAY_MS) + 1;
  // Période rapide : « 30 jours · 12 août – 10 sept. 2026 » ;
  // plage libre : « 12 août – 10 sept. 2026 · 30 jours ».
  const daysLabel = `${days} jour${days > 1 ? 's' : ''}`;
  const title = isCustom
    ? rangeLabel(range)
    : (PERIODS.find((pd) => pd.key === filter.period)?.label || '');
  const dates = isCustom ? daysLabel : rangeLabel(range);

  const prev = shiftFilter(filter, -1);
  const next = shiftFilter(filter, +1);

  const reset = () => { onChange(DEFAULT_FILTER); setRangeOpen(false); };
  const openCalendar = () => {
    setDraftRange([range.start, range.end]);
    setRangeOpen(true);
  };
  // Jour de début puis jour de fin → appliqué aussitôt, le calendrier se ferme.
  const pickRange = (r) => {
    setDraftRange(r);
    if (r[0] && r[1]) {
      onChange({ start: isoDay(r[0]), end: isoDay(r[1]) });
      setRangeOpen(false);
    }
  };
  const [dStart, dEnd] = draftRange;
  // Aide de sélection : après le 1er clic, seul le début est posé (fin vide).
  const hint = dStart && !dEnd
    ? `Début : ${fmtShort(dStart, true)} — cliquez le jour de fin`
    : 'Cliquez le jour de début, puis le jour de fin';

  return (
    <>
      {/* Rangée d'outils — même cadre que /seller-history : chip de la période
          en cours, lien Réinitialiser rouge quand une plage libre est posée,
          bouton Filtres 48 px avec badge compteur. */}
      <div className="flex gap-3 mb-3 justify-end items-center flex-wrap">
        {extra}
        <span className="chip-x gap-2 rounded px-2.5 py-[3px]" aria-live="polite">
          {title}<span className="font-normal text-gray-500"> · {dates}</span>
        </span>
        {isCustom && (
          <button type="button" onClick={reset} className="link-reset gap-1.5 px-3 py-1.5 text-sm cursor-pointer">
            Réinitialiser
          </button>
        )}
        <button
          type="button"
          onClick={() => { setShowFilters((v) => !v); setRangeOpen(false); }}
          aria-expanded={showFilters}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
        >
          <IcFilters className="text-sm" /> Filtres
          {isCustom && <span className="bdg-count static ml-2">1</span>}
        </button>
      </div>

      {showFilters && (
        <div className="flt-card rounded-2xl p-4 mb-4">
          {/* UNE rangée (owner 2026-09-10) : pilules de période rapide à gauche
              (design Réservations › Produits), « Plage personnalisée » + ‹ champ ›
              + Réinitialiser à droite ; se replie sur 2 lignes en étroit. Aucune
              pilule active quand une plage libre est posée (le champ la montre). */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Période rapide">
              {PERIODS.map((pd) => {
                const active = !isCustom && filter.period === pd.key;
                return (
                  <button
                    key={pd.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => { onChange({ period: pd.key }); setRangeOpen(false); }}
                    className={`rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                      active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pd.label}
                  </button>
                );
              })}
            </div>
            {/* ‹ période › — chevrons au kit « navigation par jour » (.btn-day) ;
                le champ ouvre le calendrier de plage défilant. */}
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <span className="flt-label mb-0 mr-1 whitespace-nowrap">Plage personnalisée</span>
              <button
                type="button"
                onClick={() => prev && onChange(prev)}
                disabled={!prev}
                aria-label="Période précédente"
                title="Période précédente"
                className="btn-day h-12 w-10 shrink-0"
              >
                <IcChevronLeft size={12} />
              </button>
              <HintContext.Provider value={hint}>
              <DatePicker
                selectsRange
                startDate={dStart}
                endDate={dEnd}
                onChange={pickRange}
                open={rangeOpen}
                onInputClick={() => (rangeOpen ? setRangeOpen(false) : openCalendar())}
                onClickOutside={() => setRangeOpen(false)}
                maxDate={new Date()}
                locale="fr"
                dateFormat="dd/MM/yyyy"
                monthsShown={MONTHS_SHOWN}
                showPreviousMonths
                popperClassName="stats-range-popper"
                popperPlacement="bottom-start"
                renderCustomHeader={CalendarHeader}
                calendarContainer={CalendarContainer}
                customInput={(
                  <RangeField
                    label={title}
                    dates={dates}
                    isCustom={isCustom}
                    open={rangeOpen}
                    onClear={reset}
                  />
                )}
              />
              </HintContext.Provider>
              <button
                type="button"
                onClick={() => next && onChange(next)}
                disabled={!next}
                aria-label="Période suivante"
                title="Période suivante"
                className="btn-day h-12 w-10 shrink-0"
              >
                <IcChevronRight size={12} />
              </button>
              {/* Bouton Réinitialiser de la carte (owner 2026-09-10 : « return
                  button réinitialiser ») — même kit btn-db qu'avant la refonte. */}
              <button
                type="button"
                onClick={reset}
                className="btn-db gap-1.5 px-3 py-2.5 text-sm transition"
              >
                <IcUndo className="text-xs" /> Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
